export { onHydrationEnd };
import type { PageContextClient } from "vike/types";

import { installIcpLinkInterceptor } from "@/5-shared/routing";
import { startCalendarMock } from "@/6-api/mockCalendar";

function onHydrationEnd(_pageContext: PageContextClient) {
  // Dev-only: iCloud CalDAV is unauthorized on localhost, mock the calendar API.
  if (import.meta.env.DEV) void startCalendarMock();

  // ICP attribution: propagate `?icp=` into internal navigation. Installed
  // eagerly (not behind the analytics idle) so the first click already carries it.
  installIcpLinkInterceptor();

  const whenIdle = (fn: () => void, timeout: number) => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(fn, { timeout });
    } else {
      setTimeout(fn, timeout);
    }
  };

  // Sentry and analytics are non-essential. Load them lazily via guarded dynamic
  // imports and swallow any failure: an adblocker or network error that blocks
  // these modules must never break hydration, client routing, or animations.

  // Sentry off the synchronous hydration frame (a TBT contributor on every
  // route) but on a short idle so error capture still starts within ~1s.
  whenIdle(() => {
    void import("@/3-features/measurement/errorMonitoring")
      .then(({ initErrorMonitoring }) => initErrorMonitoring())
      .catch(() => {});
  }, 1000);

  // Analytics is non-essential, let it wait for a longer idle window.
  whenIdle(() => {
    void import("@/3-features/measurement/metrics")
      .then(({ initGa4, trackPageView }) => {
        initGa4();
        trackPageView();
      })
      .catch(() => {});
  }, 3000);
}
